import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router"
import { getServerConsole, sendServerCommand } from "../api/console.js"
import { friendlyError } from "../api/errorMessages.js"
import { LogView } from "../components/servers/LogView.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { useToast } from "../components/ui/Toast.js"
import { Coachmark } from "../components/onboarding/Coachmark.js"
import { useFirstVisit } from "../components/onboarding/useFirstVisit.js"
import { getHint } from "../components/onboarding/hints.js"

const POLL_INTERVAL_MS = 4000

/**
 * `/servers/[id]/console` (FR seccion 19 item 6). Polling, no WebSocket
 * push — ver la nota de diseño en routes/console.ts: un tail en vivo real
 * necesita que server-manager publique deltas a NATS y el Gateway los
 * reenvíe por WS, un diseño nuevo que queda para Architect. Esto reusa
 * el mismo request/reply ya establecido (servers.queries.console_read),
 * refrescado cada 4s desde el cliente.
 */
export function ServerConsolePage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [lines, setLines] = useState<string[]>([])
  const [loaded, setLoaded] = useState(false)
  const [command, setCommand] = useState("")
  const [sending, setSending] = useState(false)
  const toast = useToast()
  const [isFirstVisit, dismissFirstVisit] = useFirstVisit("server-console")
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!serverId) return
    let cancelled = false

    const poll = () => {
      getServerConsole(serverId)
        .then(({ lines }) => {
          if (!cancelled) {
            setLines(lines)
            setLoaded(true)
          }
        })
        .catch(() => {
          /* silencioso: se reintenta en el próximo ciclo, no se interrumpe la vista con un error de un solo poll fallido */
        })
    }

    poll()
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [serverId])

  async function handleSend() {
    if (!command.trim() || !serverId) return
    setSending(true)
    try {
      await sendServerCommand(serverId, command.trim())
      setCommand("")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setSending(false)
    }
  }

  const content = (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <LogView lines={lines} />
      <div style={{ display: "flex", gap: "var(--space-2)" }}>
        <div style={{ flex: 1 }}>
          <Input
            label="Comando"
            placeholder="Escribí un comando y presioná Enter…"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSend()
            }}
          />
        </div>
        <Button loading={sending} disabled={!command.trim()} onClick={handleSend}>
          Enviar
        </Button>
      </div>
    </div>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Consola</h1>
        <p style={{ color: "var(--text-muted)" }}>Salida reciente del servidor, actualizada cada pocos segundos.</p>
      </div>
      {!loaded ? (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Cargando…</p>
      ) : isFirstVisit ? (
        <Coachmark title={getHint("server-console").title} description={getHint("server-console").description} onDismiss={dismissFirstVisit}>
          {content}
        </Coachmark>
      ) : (
        content
      )}
    </div>
  )
}
